import compact from 'lodash/compact'

import removeUnauthorizedField from 'app/utils/removeUnauthorizedField'
import {
    DTOCardFormField, FieldInCard, FormPart,
} from 'app/types/form.types'

import {
    Fee, LaneFeesDetail,
} from 'app/Apps/Pricing/Pricing.types'

import {
    LaneFeesStatus, RangeType,
} from 'app/enums/label.enums'

import {
    ReturnedReasons,
} from 'app/shared-components/LaneFees/LaneFeesForm/reasons.types'

type Args = {
    ccy: string,
    fxRate: { id?: number },
    fees: Fee[],
    status: string,
    tempRange: string,
    originalStatus: string,
    editingNotAllowed: boolean,
    getAllowedValues: (enumType: Record<string, string>, show: boolean) => Record<string, string>,
    onReviewLaneFeesReturnReasonClick: () => void,
    hasReturnReason?: boolean,
    onLaneFeesRejectReasonClick?: () => void,
    disabled?: boolean,
    truckingRequired?: boolean,
}

type LaneFeesFormFields = keyof LaneFeesDetail

const getLaneFeesFields = ({
    ccy,
    fxRate,
    fees,
    status,
    tempRange,
    originalStatus,
    editingNotAllowed,
    getAllowedValues,
    onReviewLaneFeesReturnReasonClick,
    hasReturnReason,
    onLaneFeesRejectReasonClick,
    disabled,
    truckingRequired,
}: Args):
    FormPart<LaneFeesFormFields>[] => {
    const rejectedReasonLink = {
        name: 'rejectedReason',
        componentName: 'LinkToClick',
        onClick: onLaneFeesRejectReasonClick,
        labelKey: 'LaneFees.rejectedReasonReview',
    }

    const reviewLaneFeesReturnReason = {
        name: 'reviewLaneFeesReturnReason',
        componentName: 'LinkToClick',
        onClick: onReviewLaneFeesReturnReasonClick,
        labelKey: 'LaneFees.reviewLaneFeesReturnReason',
    }

    const generalFields: FieldInCard<LaneFeesFormFields>[] = [
        {
            id: 'column1',
            fields: compact([
                {
                    name: 'laneFeesStatus',
                    labelKey: 'LaneFeesUpdateDto.laneFeesStatus',
                    componentName: 'StatusTransitionSelector',
                    originalStatus,
                    allowedValues: getAllowedValues(LaneFeesStatus, true),
                    useDropdown: true,
                    disabled: true,
                    required: true,
                },
                onLaneFeesRejectReasonClick ? rejectedReasonLink : null,
                hasReturnReason ? reviewLaneFeesReturnReason : null,
                {
                    name: 'validFrom',
                    labelKey: 'LaneFeesUpdateDto.validFrom',
                    componentName: 'DateSelect',
                    disabled: disabled || editingNotAllowed,
                    required: true,
                },
                {
                    name: 'validTo',
                    labelKey: 'LaneFeesUpdateDto.validTo',
                    componentName: 'DateSelect',
                    disabled: disabled || editingNotAllowed,
                },
                {
                    name: 'pricingNotes',
                    labelKey: 'LaneFeesUpdateDto.pricingNotes',
                    componentName: 'LongText',
                    rows: 2,
                    disabled: disabled || editingNotAllowed,
                },
            ]),
        },
        {
            id: 'column2',
            fields: [
                {
                    name: 'returnReason',
                    labelKey: 'LaneFeesUpdateDto.rejectedReason',
                    componentName: 'EnumSelector',
                    required: true,
                    useDropdown: true,
                    allowedValues: getAllowedValues(ReturnedReasons, false),
                    noSort: true,
                },
                {
                    name: 'returnReasonComment',
                    className: 'rejectedReasonComment',
                    componentName: 'LongText',
                    labelKey: 'LaneFeesUpdateDto.returnReasonComment',
                    required: true,
                },
                {
                    name: 'rentalDays',
                    labelKey: 'LaneFeesUpdateDto.rentalDays',
                    componentName: 'IntegerInput',
                    disabled: disabled || editingNotAllowed,
                },
                {
                    name: 'logisticsDays',
                    labelKey: 'LaneFeesUpdateDto.logisticsDays',
                    componentName: 'IntegerInput',
                    disabled: disabled || editingNotAllowed,
                },
                {
                    name: 'minOrder',
                    labelKey: 'LaneFeesUpdateDto.minOrder',
                    className: 'minOrder',
                    componentName: 'IntegerInput',
                    disabled: disabled || editingNotAllowed,
                },
                {
                    name: 'supportingDocuments',
                    labelKey: 'LaneFeesUpdateDto.supportingDocuments',
                    componentName: 'AttachmentsObjectSelector',
                    disabled: disabled || editingNotAllowed,
                },
            ],
        },
        {
            id: 'column3',
            fields: [
                {
                    name: 'commentForCustomer',
                    componentName: 'LongText',
                    labelKey: 'LaneFeesDto.commentForCustomer',
                    disabled: disabled || editingNotAllowed,
                },
                {
                    name: 'commentForBilling',
                    componentName: 'LongText',
                    labelKey: 'OrderBillingInformationView.laneFeesCommentForBilling',
                    disabled: disabled || editingNotAllowed,
                },
                {
                    name: 'requiresManualBillingReview',
                    componentName: 'Checkbox',
                    labelKey: 'OrderBillingInformationView.requiresManualBillingReview',
                    disabled: disabled || editingNotAllowed,
                },
                {
                    name: 'isPerformanceQualification',
                    componentName: 'Checkbox',
                    labelKey: 'Order.isPerformanceQualification',
                    disabled: disabled || editingNotAllowed,
                },
            ],
        },
    ]

    const tempReasonFields:
        FieldInCard<LaneFeesFormFields>[] = generalFields.map((currentFields) => {
            if (currentFields.id === 'column2') {
                return {
                    ...currentFields,
                    fields: removeUnauthorizedField(
                        removeUnauthorizedField(currentFields.fields, [
                            'rejectedReason',
                            'rejectedReasonHint',
                            'rejectedReasonComment',
                        ], status === LaneFeesStatus.REJECTED),
                        [
                            'returnReason',
                            'returnReasonComment',
                        ],
                        originalStatus === LaneFeesStatus.SUBMITTED
                    && status === LaneFeesStatus.IN_PROGRESS,
                    ),
                }
            }
            return currentFields
        })

    const generalSectionFields:
        DTOCardFormField<LaneFeesFormFields> = {
            title: 'General',
            className: 'general',
            fields: tempReasonFields,
        }

    const feeDays: DTOCardFormField<LaneFeesFormFields>[] = [{
        id: 'FeeDays',
        title: 'Fee Days',
        className: 'feeDays',
        fields: [
            {
                id: 'column1',
                fields: [
                    {
                        name: 'expressOrderTimeline',
                        componentName: 'IntegerInput',
                        disabled: editingNotAllowed,
                        required: true,
                        labelKey: 'LaneGroupCreateOrDuplicateDto.expressOrderTimeline',
                    },
                    {
                        name: 'expressDaysType',
                        componentName: 'EnumSelector',
                        allowedValues: getAllowedValues(RangeType, false),
                        useDropdown: true,
                        disabled: editingNotAllowed,
                        required: true,
                        labelKey: 'LaneGroupCreateOrDuplicateDto.expressDaysType',
                    },
                ],
            },
            {
                id: 'column2',
                fields: [
                    {
                        name: 'postponementFeeTimeline',
                        componentName: 'IntegerInput',
                        disabled: editingNotAllowed,
                        required: true,
                        labelKey: 'LaneGroupCreateOrDuplicateDto.postponementFeeTimeline',
                    },
                    {
                        name: 'postponementDaysType',
                        componentName: 'EnumSelector',
                        allowedValues: getAllowedValues(RangeType, false),
                        useDropdown: true,
                        disabled: editingNotAllowed,
                        required: true,
                        labelKey: 'LaneGroupCreateOrDuplicateDto.postponementDaysType',
                    },
                ],
            },
        ],
    }]

    const truckingCapacity: FieldInCard<LaneFeesFormFields> = {
        id: 'truckingCapacity',
        title: 'Max Containers Per Truck / Van',
        className: 'truckingCapacity',
        fields: [{
            name: 'truckingCapacity',
            labelKey: 'LaneFeesFromLaneGroupView.truckingCapacity',
            componentName: 'TruckCapacity',
            tempRange,
            disabled: editingNotAllowed,
            required: truckingRequired,
        }],
    }

    const feesField: FieldInCard<LaneFeesFormFields> = {
        title: 'Fees',
        className: 'fees',
        fields: [{
            name: 'fees',
            componentName: 'Fees',
            labelKey: 'LaneFeesUpdateDto.fees',
            fxRate,
            currency: ccy,
            tempRange,
            value: fees,
            disabled: editingNotAllowed,
            showSkyNetSpreadSheet: true,
        }],
    }

    return [
        generalSectionFields,
        truckingCapacity,
        ...feeDays,
        feesField,
    ]
}

export default getLaneFeesFields
